import {
  ExtendedModel,
  frozen,
  modelAction,
  type SnapshotOutOf,
  tProp,
  types,
} from "mobx-keystone";
import { api } from "../../../orpc/client";
import { GlobalTModel } from "../../lib/models/global-t-model";
import LoaderModel from "./loader";
import BankModel from "./models/static/bank/model.bank";
import CardNetworkModel from "./models/static/card-network/model.card";
import PluginDefModel from "./models/static/plugin-def/model.plugin-def";
import { globalNS } from "./ns.global";

@globalNS.store
export default class GlobalStore extends ExtendedModel(GlobalTModel, {
  loader: tProp(
    LoaderModel,
    () =>
      new LoaderModel({
        loading: false,
        meta: undefined,
        canCancel: undefined,
      }),
  ),
  plugins: tProp(types.frozen(types.mapObject(PluginDefModel)), () =>
    frozen({}),
  ).withSetter(),
  banks: tProp(types.mapObject(BankModel), () => ({})).withSetter(),
  cardNetworks: tProp(types.frozen(types.mapObject(CardNetworkModel)), () =>
    frozen({}),
  ).withSetter(),
}) {
  protected omittedKeys: (keyof SnapshotOutOf<this>)[] = ["loader"];

  // Session-scoped guard so `hydrateBanks` only fetches once per client
  // instance. Not a tProp because it is ephemeral and shouldn't be persisted
  // in snapshots.
  private _banksHydrated = false;

  get ready(): boolean {
    throw new Error("Method not implemented.");
  }

  /**
   * Lazily fetch the set of banks that have an SVG logo and populate
   * `globalStore.banks`. Safe to call repeatedly — only the first call per
   * session triggers a network request. The flag is set BEFORE the request
   * so a transient render storm cannot kick off concurrent fetches; on
   * failure we leave it set (logged) rather than clear-and-retry, which
   * would cause a tight retry loop on a deterministic backend error.
   */
  async hydrateBanks(): Promise<void> {
    if (this._banksHydrated) return;
    this._banksHydrated = true;
    try {
      const banks = await api.static.bank.get({
        svg: { not: null },
        enabled: true,
      });
      const next: Record<string, BankModel> = {};
      for (const b of banks) {
        next[b.name] = new BankModel({
          name: b.name,
          svg: b.svg,
          countryCode: b.countryCode,
          enabled: b.enabled ?? true,
          routingNumbers: b.routingNumbers ?? [],
        });
      }
      this.setBanks(next);
    } catch (error) {
      console.error("Error hydrating banks", error);
    }
  }

  /**
   * Insert (or replace) a single bank entry into `globalStore.banks` so that
   * subsequent lookups by any of its routing numbers can be served from the
   * store without an extra network round-trip.
   */
  @modelAction
  cacheBank(plain: {
    name: string;
    svg: string | null;
    routingNumbers: string[];
  }): void {
    const next: Record<string, BankModel> = {
      ...this.banks,
      [plain.name]: new BankModel({
        name: plain.name,
        svg: plain.svg,
        countryCode: null,
        enabled: true,
        routingNumbers: plain.routingNumbers,
      }),
    };
    this.setBanks(next);
  }
}
